import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, Rocket, Youtube, Instagram, Film } from 'lucide-react';
import Button from './Button';
import { getCategories } from '../services/careerService';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'All Careers', path: '/careers' },
    { name: 'Latest Shorts', path: '/shorts' },
    { name: 'STEM', path: '/categories/stem' },
  ];

  const youtubeUrl = "https://youtube.com/@FuturePathTeens?utm_source=futurepathteens&utm_medium=website&utm_campaign=nav_social";

  return (
    <header className="sticky top-0 z-50 w-full bg-brand-bg/95 backdrop-blur-md border-b border-brand-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-brand-cyan to-brand-green flex items-center justify-center transition-transform group-hover:scale-110">
              <Rocket className="w-5 h-5 text-brand-dark" />
            </div>
            <span className="font-heading font-bold text-lg text-brand-white tracking-tight">
              FuturePath <span className="text-brand-cyan">Teens</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-brand-cyan ${
                  location.pathname === link.path ? 'text-brand-cyan' : 'text-brand-grey1'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <a 
              href={youtubeUrl}
              target="_blank" 
              rel="noreferrer"
            >
               <Button size="sm" variant="outline" className="border-brand-cyan/50 hover:bg-brand-cyan/10">
                 <Youtube className="w-4 h-4 mr-2" />
                 YouTube
               </Button>
            </a>
          </nav>

          <button
            className="md:hidden p-2 text-brand-grey1 hover:text-brand-cyan transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 w-full bg-brand-dark border-b border-brand-border shadow-2xl animate-fade-in-down">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                   location.pathname === link.path 
                   ? 'bg-brand-cyan/10 text-brand-cyan' 
                   : 'text-brand-grey1 hover:bg-brand-light hover:text-brand-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 mt-4 border-t border-brand-border">
                <a 
                href={youtubeUrl}
                target="_blank" 
                rel="noreferrer"
                className="flex items-center justify-center w-full px-4 py-3 rounded-lg border border-brand-border text-brand-grey1 hover:text-brand-white hover:border-brand-cyan transition-colors"
                >
                <Youtube className="w-5 h-5 mr-2" />
                Visit YouTube Channel
                </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const categories = getCategories();
  const youtubeUrl = "https://youtube.com/@FuturePathTeens?utm_source=futurepathteens&utm_medium=website&utm_campaign=footer_social";

  return (
    <footer className="bg-brand-dark border-t border-brand-border pt-12 pb-8 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div className="col-span-1 md:col-span-1">
             <Link to="/" className="flex items-center space-x-2 mb-4 group">
               <div className="w-6 h-6 rounded bg-brand-cyan flex items-center justify-center">
                  <Rocket className="w-3 h-3 text-brand-dark" />
               </div>
               <span className="font-heading font-bold text-lg text-brand-white">
                FuturePath Teens
              </span>
            </Link>
            <p className="text-brand-grey2 text-sm leading-relaxed">
              Helping the next generation discover their dream careers through fast, clear, and inspiring guides.
            </p>
          </div>
          
          <div>
            <h4 className="font-heading font-semibold text-brand-white mb-4">Explore</h4>
            <ul className="space-y-2 text-sm text-brand-grey2">
              <li><Link to="/careers" className="hover:text-brand-cyan transition-colors">All Careers</Link></li>
              <li><Link to="/shorts" className="hover:text-brand-cyan transition-colors">Latest Shorts</Link></li>
              {categories.slice(0, 3).map(cat => (
                 <li key={cat.id}>
                    <Link to={`/categories/${cat.id}`} className="hover:text-brand-cyan transition-colors">
                        {cat.label}
                    </Link>
                 </li>
              ))}
            </ul>
          </div>

          <div>
             <h4 className="font-heading font-semibold text-brand-white mb-4">Support</h4>
            <ul className="space-y-2 text-sm text-brand-grey2">
              <li><Link to="/sponsor" className="hover:text-brand-cyan transition-colors">Sponsor & Advertise</Link></li>
              <li><span className="cursor-pointer hover:text-brand-cyan transition-colors text-xs">Privacy Policy</span></li>
              <li><span className="cursor-pointer hover:text-brand-cyan transition-colors text-xs">Terms of Service</span></li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-brand-white mb-4 text-sm uppercase tracking-widest">Connect</h4>
            <div className="flex space-x-4">
              <a 
                href={youtubeUrl}
                target="_blank" 
                rel="noreferrer" 
                className="w-10 h-10 rounded-full bg-brand-bg border border-brand-border flex items-center justify-center text-brand-grey2 hover:text-brand-cyan hover:border-brand-cyan transition-all"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-brand-bg border border-brand-border flex items-center justify-center text-brand-grey2 hover:text-brand-cyan hover:border-brand-cyan transition-all"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
        
        <div className="border-t border-brand-border pt-8 text-center text-sm text-brand-grey2 flex flex-col sm:flex-row justify-between items-center">
          <p>&copy; {new Date().getFullYear()} FuturePath Teens.</p>
          <p className="mt-2 sm:mt-0 text-xs">Designed for the Next Generation.</p>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  return (
    <div className="min-h-screen flex flex-col bg-brand-bg text-brand-grey1">
      <Header />
      <main className="flex-grow w-full">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
